import { useEffect, useState, FunctionComponent, isValidElement, cloneElement } from 'react'
import type { Location } from 'history'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import loadable, { LoadableComponent } from '@loadable/component'
import * as routerActions from 'connected-react-router'
import { stringify, parse } from 'querystringify'
import { omit } from 'lodash'
import { Helmet } from 'react-helmet'

// Components
import { IconCloseFat, Spinner } from '@tracklib/kit'

// Modals
import { MODALS } from 'components/modal/location/modals'

// Utils
import getScrollbarSize from 'lib/scrollbarSize'

import styles from './location.css'

// To require logged in status for modal. wrap modal content in userIsAuthenticatedOrShowLogin.
// Also, location={location} needs to be passed to component for redirect to work
const Modal = loadable(() => import('components/modal'))

type LocationModalProps = {
  fullscreen?: boolean
}

const LocationModal = ({ fullscreen }: LocationModalProps) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [CurrentContent, setCurrentContent] = useState<
    | LoadableComponent<{
        onClose: () => void
        location?: Location
        fallback?: JSX.Element
      }>
    | undefined
    | FunctionComponent
  >(undefined)
  const query = parse(location.search) as { loop: string; url: string; collectionId: string; modal: string }
  const onReplace = (urlObj: { pathname: string; hash: string; search: string }) => dispatch(routerActions.replace(urlObj))
  const allModals = MODALS(query)

  useEffect(() => {
    const modal = allModals[query.modal as keyof typeof allModals]
    if (modal?.component) {
      setCurrentContent(modal?.component)
    }
  }, [query.modal]) // eslint-disable-line

  const handleClose = () => {
    const newLocation = {
      pathname: location.pathname,
      hash: '',
      search: stringify(
        omit(query, [
          'action',
          'changed',
          'collectionId',
          'email',
          'full',
          'invite',
          'invitekey',
          'licensee',
          'loop',
          'modal',
          'newsletter',
          'pstatus',
          'ref',
          'revenueReportId',
          'send_token',
          'songId',
          'token',
          'trackId',
          'uid',
          'url',
          'usersong',
          'userSongId',
          'utm_campaign',
          'utm_medium',
          'utm_source',
        ]),
      ),
    }
    onReplace(newLocation)
  }

  const modals = Object.keys(allModals)
  const modal = allModals[query.modal as keyof typeof allModals]
  if (!modal) {
    return null
  }

  const scrollbarSize = getScrollbarSize()
  const isVisible = query.modal ? modals.includes(query.modal) : false
  const contentProps = { onClose: handleClose, location, fallback: <Spinner minHeight={400} delay={0} /> }
  return (
    <Modal
      modalTitle={modal?.title}
      modalSize={modal?.size}
      onClose={handleClose}
      isOpen={isVisible}
      closeOnEscKey={modal.noCloseOnEscAndBackdrop}
      closeOnBackdropClick={modal.noCloseOnEscAndBackdrop}
      fullscreen={fullscreen}
      mobileFullscreen={modal.mobileFullscreen}
      mobileAlignment={modal?.mobileAlignment}
      transitionEnabled={modal?.transitionEnabled}
      noPadding={modal?.noPadding}
    >
      {isVisible && (
        <Helmet>
          <meta name="robots" content="Noindex" />
          <body data-modal="true" style={{ paddingRight: `${scrollbarSize}px` }} />
        </Helmet>
      )}
      <button onClick={handleClose} className={styles.closeButton} datatesting="button_close_modal">
        <IconCloseFat />
      </button>
      {/* NOTE(Kitti): Auth wrapped modals are rendered components for some stupid reason so need 2 ways of handling this. WILL FIX! */}
      {isValidElement(CurrentContent) ? cloneElement(CurrentContent, contentProps) : CurrentContent && <CurrentContent {...contentProps} />}
    </Modal>
  )
}

export default LocationModal
